import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { getCustomers } from '../../shared/common.api';

// CIN-6288: search a customer by name, email, OR phone in one box. Reuses the
// approach proven in the reviews "Link to Customer" flow: the customer endpoint
// has no combined search param, so we detect the input type and send the right
// one (phone digits / email / else name). Extracted here as a reusable control
// so the Create Task modal (and future callers) don't re-implement it.
const looksLikePhone = s => /\d/.test(s) && !/[a-zA-Z@]/.test(s);
const looksLikeEmail = s => s.includes('@');
const customerLabel = c => c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || c.phone || 'Customer' : '';
export const CustomerSearchSelect = ({
  companyId,
  value,
  onChange,
  disabled,
  label = 'Customer (optional)',
  placeholder = 'Search by name, email, or phone'
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Debounced, type-aware fetch. Recreated only when the company changes.
  const search = useMemo(() => {
    let timer;
    return text => {
      clearTimeout(timer);
      const trimmed = (text || '').trim();
      if (!trimmed || !companyId) {
        setOptions([]);
        return;
      }
      timer = setTimeout(() => {
        const param = looksLikePhone(trimmed) ? {
          phone: trimmed.replace(/[^0-9]/g, '')
        } : looksLikeEmail(trimmed) ? {
          email: trimmed
        } : {
          name: trimmed
        };
        setLoading(true);
        getCustomers({
          company: companyId,
          ordering: 'first_name',
          limit: 25,
          ...param
        }).then(({
          data
        }) => setOptions(data?.results || data || [])).catch(() => setOptions([])).finally(() => setLoading(false));
      }, 400);
    };
  }, [companyId]);
  return <Autocomplete disabled={disabled} value={value || null} onChange={(_e, selected) => onChange(selected)} inputValue={inputValue} onInputChange={(_e, text, reason) => {
    setInputValue(text);
    if (reason === 'input') search(text);
  }} options={options} loading={loading} getOptionLabel={customerLabel} isOptionEqualToValue={(option, val) => option.id === val.id} filterOptions={x => x} // results are already server-filtered; don't re-filter
  noOptionsText={inputValue ? 'No matching customers' : 'Type a name, email, or phone'} renderOption={(props, c) => <Box component="li" {...props} key={c.id}>
          <Box>
            <Typography sx={{
        fontSize: '14px'
      }}>{customerLabel(c)}</Typography>
            <Typography sx={{
        fontSize: '12px',
        color: '#848484'
      }}>
              {[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact info'}
            </Typography>
          </Box>
        </Box>} renderInput={params => <TextField {...params} label={label} placeholder={placeholder} size="small" InputProps={{
    ...params.InputProps,
    endAdornment: <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
  }} />} />;
};